<template>
  <div
    v-if="answers.length > 0"
    class="border border-action-brand-low rounded-sm bg-static-default-low"
  >
    <RevAccordionList :has-external-borders="false">
      <RevAccordionItem
        data-qa="trade-in-answer-summary"
        data-test="trade-in-answer-summary"
        :collapsed="accordionState"
        @click="toggleAccordion"
      >
        <template #title>
          <p class="text-static-default-hi body-1">
            {{ i18n(translations.title) }}
          </p>
        </template>

        <template #description>
          <AccordionTransition :condition="accordionState === 'collapsed'">
            <span
              class="line-clamp-2"
              data-qa="trade-in-answer-summary-description"
              data-test="trade-in-answer-summary-description"
            >
              {{ descriptionText }}
            </span>
          </AccordionTransition>
        </template>

        <template #body>
          <div
            class="flex flex-col gap-16"
            data-qa="trade-in-answer-summary-rows"
            data-test="trade-in-answer-summary-rows"
          >
            <div
              v-for="{ question, answer, id } in summaryItems"
              :key="id"
              data-qa="trade-in-answer-summary-row"
            >
              <p class="text-static-default-low caption">{{ question }}</p>
              <p class="text-static-default-hi body-2">{{ answer }}</p>
            </div>
          </div>
        </template>
      </RevAccordionItem>
    </RevAccordionList>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevAccordionItem } from '@ds/components/AccordionItem'
import { CollapsedStatesEnum } from '@ds/components/AccordionItem/AccordionItem.constant'
import { RevAccordionList } from '@ds/components/AccordionList'

import type { Answer, Question } from '~/api/tradein-apac/types'
import AccordionTransition from '~/scopes/product-core/components/presentationals/CustomizationFunnel/components/LargePicker/components/AccordionTransition/AccordionTransition.vue'

import translations from './TradeInAnswerSummary.translations'

interface TradeInAnswerSummaryProps {
  answers: Answer[]
  questions: Question[]
}

const { questions, answers } = defineProps<TradeInAnswerSummaryProps>()

const i18n = useI18n()

const { countryCode } = useMarketplace().market

const accordionState = ref(CollapsedStatesEnum.Collapsed)

const toggleAccordion = () => {
  accordionState.value =
    accordionState.value === CollapsedStatesEnum.Collapsed
      ? CollapsedStatesEnum.Expanded
      : CollapsedStatesEnum.Collapsed
}

const summaryItems = computed(() =>
  questions.map((question, index) => {
    const answer = answers[index]
    const optionLabel = question.options.find(
      (option) => option.value === answer?.value,
    )?.label

    return {
      id: question.key,
      question: question.title,
      answer: optionLabel ?? answer?.value ?? '',
    }
  }),
)

const DESCRIPTION_MAX_ANSWERS = countryCode === MarketCountryCode.AU ? 4 : 5
const descriptionText = computed(() =>
  summaryItems.value
    .slice(0, DESCRIPTION_MAX_ANSWERS)
    .map((row) => row.answer)
    .join('・'),
)
</script>
